import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { AceTranslatePipe, AppLanguageService, BreadcrumbComponent, ButtonComponent, InputComponent, TabPanelComponent, TabViewComponent, TranslationService } from '@ace/ace';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { AssessmentDto, CourseDto, ExamCorrectionSummaryDto, InstructorDto } from '../../models/instructor.models';
import { PageRefreshService } from '../../core/services/page-refresh.service';
import { AssessmentService } from '../../services/assessment.service';
import { CourseService } from '../../services/course.service';
import { InstructorService } from '../../services/instructor.service';
import { MetricCardsComponent } from '../../shared/components/metric-cards/metric-cards.component';
import { parseBackendDateTime } from '../../shared/utils/date-time-display.util';

type AssessmentTypeFilter = 'ALL' | 'ASSIGNMENT' | 'EXAM' | 'QUIZ';

@Component({
  selector: 'app-my-exam-corrections',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AceTranslatePipe, BreadcrumbComponent, ButtonComponent, InputComponent, MetricCardsComponent, TabViewComponent, TabPanelComponent],
  templateUrl: './my-exam-corrections.component.html',
  styleUrl: './my-exam-corrections.component.scss',
})
export class MyExamCorrectionsComponent implements OnInit, OnDestroy {
  readonly filterForm = new FormGroup({
    searchTerm: new FormControl<string>('', { nonNullable: true }),
  });
  instructor: InstructorDto | null = null;
  cards: ExamCorrectionSummaryDto[] = [];
  assessmentTypeTabs: Array<{ value: AssessmentTypeFilter; label: string; count: number }> = [];
  loading = false;
  errorMessage: string | null = null;
  assessmentType: 'QUIZ' | 'EXAM' | 'ASSIGNMENT' = 'EXAM';
  private readonly destroy$ = new Subject<void>();
  private appLocale: 'en' | 'ar' | 'fr' | 'de' = 'en';

  constructor(
    private readonly assessmentService: AssessmentService,
    private readonly courseService: CourseService,
    private readonly instructorService: InstructorService,
    private readonly router: Router,
    private readonly translationService: TranslationService,
    private readonly pageRefreshService: PageRefreshService,
    private readonly appLanguageService: AppLanguageService,
  ) {}

  ngOnInit(): void {
    this.appLocale = this.normalizeLanguage(localStorage.getItem('appLang'));
    this.appLanguageService.selectedLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language: any) => {
        this.appLocale = this.normalizeLanguage(language?.isoCode ?? language);
        this.rebuildAssessmentTypeTabs();
      });

    this.loadCorrections();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((event) => this.isCorrectionsRoute(event.urlAfterRedirects)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadCorrections();
      });

    this.pageRefreshService.refresh$
      .pipe(
        filter((url) => this.isCorrectionsRoute(url)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadCorrections();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get metricCards(): Array<{ label: string; value: number; description: string; icon: string; color: string }> {
    return [
      {
        label: this.t('Needs Grading'),
        value: this.needsGradingBaseCards.length,
        description: this.t('Assessments waiting for your review'),
        icon: 'ri-edit-2-line',
        color: '#f59e0b',
      },
      {
        label: this.t('Homework'),
        value: this.countNeedsGradingByType('ASSIGNMENT'),
        description: this.t('Homework waiting for grading'),
        icon: 'ri-book-open-line',
        color: '#16a34a',
      },
      {
        label: this.t('Exams'),
        value: this.countNeedsGradingByType('EXAM'),
        description: this.t('Exams waiting for grading'),
        icon: 'ri-file-list-3-line',
        color: '#dc2626',
      },
      {
        label: this.t('Quizzes'),
        value: this.countNeedsGradingByType('QUIZ'),
        description: this.t('Quizzes waiting for grading'),
        icon: 'ri-questionnaire-line',
        color: '#7c3aed',
      },
    ];
  }

  get filteredCards(): ExamCorrectionSummaryDto[] {
    return this.cards;
  }

  get needsGradingCards(): ExamCorrectionSummaryDto[] {
    return this.getNeedsGradingCardsForType('ALL');
  }

  get needsGradingBaseCards(): ExamCorrectionSummaryDto[] {
    return this.cards.filter((card) => card.workflowState === 'Needs Grading');
  }

  getNeedsGradingCardsForType(type: AssessmentTypeFilter): ExamCorrectionSummaryDto[] {
    const query = this.filterForm.controls.searchTerm.value.trim().toLowerCase();
    const visible = this.filteredCards
      .filter((card) => card.workflowState === 'Needs Grading')
      .filter((card) => type === 'ALL' || card.assessmentType === type)
      .filter((card) => {
        if (!query) return true;
        return [
          this.getCardAssessmentDisplayName(card),
          this.getCardCourseDisplayName(card),
          this.getCardScheduleDisplay(card),
          this.assessmentTypeLabel(card.assessmentType),
          card.markerRole,
        ].some((value) => value.toLowerCase().includes(query));
      });

    return [...visible].sort((left, right) => {
      const leftTime = this.getScheduleSortTime(left);
      const rightTime = this.getScheduleSortTime(right);
      if (leftTime !== rightTime) {
        return rightTime - leftTime;
      }

      return (right.pendingLearnersCount ?? 0) - (left.pendingLearnersCount ?? 0);
    });
  }

  roleBadgeClass(role: string): string {
    return role === 'SECOND_MARKER'
      ? 'role-badge role-badge--secondary'
      : 'role-badge role-badge--primary';
  }

  assessmentIconClass(type: 'QUIZ' | 'EXAM' | 'ASSIGNMENT'): string {
    if (type === 'QUIZ') return 'correction-card__icon correction-card__icon--quiz';
    if (type === 'ASSIGNMENT') return 'correction-card__icon correction-card__icon--assignment';
    return 'correction-card__icon correction-card__icon--exam';
  }

  assessmentTypeBadgeClass(type: 'QUIZ' | 'EXAM' | 'ASSIGNMENT'): string {
    if (type === 'QUIZ') return 'assessment-type-badge assessment-type-badge--quiz';
    if (type === 'ASSIGNMENT') return 'assessment-type-badge assessment-type-badge--assignment';
    return 'assessment-type-badge assessment-type-badge--exam';
  }

  assessmentTypeIcon(type: 'QUIZ' | 'EXAM' | 'ASSIGNMENT'): string {
    if (type === 'QUIZ') return 'ri-questionnaire-line';
    if (type === 'ASSIGNMENT') return 'ri-book-open-line';
    return 'ri-file-list-3-line';
  }

  getCardScheduleDisplay(card: ExamCorrectionSummaryDto): string {
    const startDate = this.formatScheduleDate(card.scheduledStartAt);
    const endDate = this.formatScheduleDate(card.scheduledEndAt);
    const startTime = this.formatScheduleTime(card.scheduledStartAt);
    const endTime = this.formatScheduleTime(card.scheduledEndAt);
    const timeRange = startTime && endTime ? `${startTime}-${endTime}` : (startTime || endTime);

    if (card.assessmentType === 'ASSIGNMENT') {
      const dateRange = startDate && endDate
        ? `${startDate} - ${endDate}`
        : (startDate || endDate);
      return [dateRange, timeRange].filter(Boolean).join(' | ') || this.t('Not scheduled');
    }

    return [startDate, timeRange].filter(Boolean).join(' | ') || this.t('Not scheduled');
  }

  assessmentTypeLabel(type: 'QUIZ' | 'EXAM' | 'ASSIGNMENT'): string {
    if (type === 'QUIZ') return this.t('Quiz');
    if (type === 'ASSIGNMENT') return this.t('Homework');
    return this.t('Exam');
  }

  openCorrections(card: ExamCorrectionSummaryDto): void {
    if (!card.assessmentId) {
      return;
    }

    void this.router.navigate(['/main/assessment/marking', card.assessmentId], {
      queryParams: { role: card.markerRole },
    });
  }

  trackByAssessmentTypeTab(_: number, tab: { value: AssessmentTypeFilter }): AssessmentTypeFilter {
    return tab.value;
  }

  trackByCorrectionCard(_: number, card: ExamCorrectionSummaryDto): string {
    return `${card.assessmentId}:${card.markerRole}`;
  }

  get pageTitle(): string {
    return this.t('Assessment Marking');
  }

  get cardActionLabel(): string {
    return this.t('Grade');
  }

  getCardAssessmentDisplayName(card: ExamCorrectionSummaryDto): string {
    const native = card.examNameNative?.trim() ?? '';
    const english = card.examName?.trim() ?? '';
    return this.shouldUseNativeNames() && native ? native : (english || native || '—');
  }

  getCardCourseDisplayName(card: ExamCorrectionSummaryDto): string {
    const native = card.courseNameNative?.trim() ?? '';
    const english = card.courseName?.trim() ?? '';
    return this.shouldUseNativeNames() && native ? native : (english || native || this.t('Course'));
  }

  private loadCorrections(): void {
    this.loading = true;
    this.errorMessage = null;

    forkJoin({
      instructor: this.instructorService.getMe(),
    }).subscribe({
      next: ({ instructor }) => {
        this.instructor = instructor;
        const correctionInstructorId = instructor.id || instructor.userId;
        this.assessmentService.getCorrectionSummary(correctionInstructorId)
          .pipe(catchError(() => of([])))
          .subscribe({
            next: (cards) => {
              this.enrichCorrectionCards(cards);
            },
            error: () => {
              this.errorMessage = this.t('Failed to load corrections');
              this.loading = false;
            }
          });
      },
      error: () => {
        this.errorMessage = this.t('Failed to load corrections');
        this.loading = false;
      },
    });
  }

  private isCorrectionsRoute(url: string): boolean {
    return url === '/main/assessment/marking' || url.startsWith('/main/assessment/marking?');
  }

  private enrichCorrectionCards(cards: ExamCorrectionSummaryDto[]): void {
    if (!cards.length) {
      this.cards = [];
      this.rebuildAssessmentTypeTabs();
      this.loading = false;
      return;
    }

    forkJoin({
      assessments: this.assessmentService.getAll().pipe(
        catchError(() => of([] as AssessmentDto[])),
      ),
      courses: this.courseService.getAll().pipe(
        catchError(() => of([] as CourseDto[])),
      ),
    }).subscribe({
      next: ({ assessments, courses }) => {
        const assessmentsById = new Map(assessments.map((assessment) => [assessment.id, assessment]));
        const coursesById = new Map(courses.map((course) => [course.id, course]));
        this.cards = cards.map((card) => {
          const assessment = assessmentsById.get(card.assessmentId);
          const course = assessment?.courseId ? coursesById.get(assessment.courseId) : undefined;
          return {
            ...card,
            examName: assessment?.nameEn?.trim() || card.examName,
            examNameNative: assessment?.nameNative?.trim() || card.examNameNative || null,
            scheduledStartAt: assessment?.scheduledStartAt ?? card.scheduledStartAt ?? null,
            scheduledEndAt: assessment?.scheduledEndAt ?? card.scheduledEndAt ?? null,
            courseName: course?.courseNameEn?.trim() || assessment?.courseName?.trim() || card.courseName || null,
            courseNameNative: course?.courseNameNative?.trim() || card.courseNameNative || null,
          };
        });
        this.rebuildAssessmentTypeTabs();
        this.loading = false;
      },
      error: () => {
        this.cards = cards;
        this.rebuildAssessmentTypeTabs();
        this.loading = false;
      },
    });
  }

  private rebuildAssessmentTypeTabs(): void {
    this.assessmentTypeTabs = [
      { value: 'ALL', label: this.t('All'), count: this.countNeedsGradingByType('ALL') },
      { value: 'ASSIGNMENT', label: this.t('Homework'), count: this.countNeedsGradingByType('ASSIGNMENT') },
      { value: 'EXAM', label: this.t('Exams'), count: this.countNeedsGradingByType('EXAM') },
      { value: 'QUIZ', label: this.t('Quizzes'), count: this.countNeedsGradingByType('QUIZ') },
    ];
  }

  private countNeedsGradingByType(type: AssessmentTypeFilter): number {
    return this.needsGradingBaseCards
      .filter((card) => type === 'ALL' || card.assessmentType === type)
      .length;
  }

  private formatScheduleDate(value?: string | null): string {
    const parsed = parseBackendDateTime(value);
    if (!parsed) return '';

    return new Intl.DateTimeFormat(this.localeTag(), {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(parsed);
  }

  private formatScheduleTime(value?: string | null): string {
    const parsed = parseBackendDateTime(value);
    if (!parsed) return '';

    return new Intl.DateTimeFormat(this.localeTag(), {
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  }

  private getScheduleSortTime(card: ExamCorrectionSummaryDto): number {
    return parseBackendDateTime(card.scheduledStartAt)?.getTime() ?? Number.NEGATIVE_INFINITY;
  }

  private t(key: string): string {
    return this.translationService.translate(key);
  }

  private shouldUseNativeNames(): boolean {
    return this.appLocale === 'ar';
  }

  private localeTag(): string {
    return this.appLocale === 'ar' ? 'ar' : this.appLocale;
  }

  private normalizeLanguage(language: unknown): 'en' | 'ar' | 'fr' | 'de' {
    const value = String(language ?? '').toLowerCase();
    if (value.startsWith('ar')) return 'ar';
    if (value.startsWith('fr')) return 'fr';
    if (value.startsWith('de')) return 'de';
    return 'en';
  }
}
